import React, { memo } from 'react';
import { Button, CircularProgress } from '@mui/material';
import classNames from 'classnames';

import CameraClipIcon from 'components/Icons/CameraClipIcon';
import WavetrakLink from 'components/WavetrakLink';
import ChevronRightNoBorder from 'components/Icons/ChevronRightNoBorder';

import styles from './ClipButton.module.scss';

const ButtonLoadingState = (
  <CircularProgress
    className={styles.loadingSpinnerIcon}
    data-testid="clips-loading-spinner"
    size={20}
  />
);

interface Props {
  disabled?: boolean;
  href?: string;
  supportsSmartRewinds?: boolean;
  dataTestId?: string;
  minimizeOnMobile?: boolean;
  btnClassName?: string;
  clipsLoading?: boolean;
  /*
   * We have to have onClick named as such here as in some cases we use this button
   * in the Information component here which automatically adds it
   * https://github.com/Surfline/wavetrak-services/blob/0efab65a823ef673b8601c1789f07a26a0a40cc4/services/kbyg-web/src/components/Information/Information.tsx#L314-L315
   * Ideally we would refactor that Information to be more flexible.
   */
  onClick?: () => void;
}

const ClipButton = ({
  href,
  disabled,
  supportsSmartRewinds,
  btnClassName = '',
  dataTestId = 'clip-button-non-paywall',
  minimizeOnMobile = false,
  clipsLoading,
  onClick,
}: Props) => (
  <Button
    className={classNames({
      [btnClassName]: true,
      [styles.clipButton]: true,
      [styles.supportsSmartClips]: supportsSmartRewinds,
      [styles.minimizedOnMobile]: minimizeOnMobile,
    })}
    data-testid={dataTestId}
    disableFocusRipple
    disableRipple
    disableTouchRipple
    disabled={disabled}
    href={href || ''}
    onClick={onClick}
    size="small"
    variant="secondary"
    {...(href && { component: WavetrakLink, prefetch: false })}
  >
    <span className={styles.clipButtonText}>
      {supportsSmartRewinds ? 'Smart Clips' : 'Find Clips'}
    </span>
    <span className={classNames([styles.buttonIcon])}>
      {clipsLoading ? ButtonLoadingState : <ChevronRightNoBorder />}
    </span>
    {minimizeOnMobile && (
      <span
        className={classNames([styles.minimizedMobileButtonIcon])}
        data-testid="mobile-clip-icon"
      >
        {clipsLoading ? ButtonLoadingState : <CameraClipIcon />}
      </span>
    )}
  </Button>
);

export default memo(ClipButton);
